import React, { useEffect, useState } from 'react'
import Toast from '../Toast';
import { doc, getDoc, collection, query, where, getDocs, Timestamp, limit, addDoc, updateDoc, writeBatch, getCountFromServer, DocumentData, documentId } from 'firebase/firestore';
import { format, startOfDay, endOfDay } from 'date-fns';

// Firebase
import { db } from '../../firebaseConfig';
import { useAuth } from '../../AuthContext';

interface FollowUp {
    id: string;
    followUp: string;
    followUpTime: Timestamp;
    isDone: boolean;
    isEdited: boolean;
    leadId: string;
    studentName: string;
    studentNumber: string;
    userId: string;
    assignedUserName: string;
}

interface FollowUpModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedStudent: {
        id: string;
        name: string;
        phone: string;
    };
}

const FollowUpModal = (
    { isOpen, onClose, selectedStudent }: FollowUpModalProps
) => {

    const { userData, currentUser, isAdmin, isManager, isStaff } = useAuth();

    // Add follow-up states
    const [loading, setLoading] = useState(false);
    const [followUpDescription, setFollowUpDescription] = useState('');
    const [followUpDateTime, setFollowUpDateTime] = useState('');
    const [toogleAddFollowUpAction, setToggleAddFollowUpAction] = useState<'add' | 'update'>('add');

    const [selectedStudentInModalId, setSelectedStudentInModalId] = useState<string>('');
    const [selectedFollowUp, setSelectedFollowUp] = useState<any>({}); // Adjust type as needed
    const [leadAssigneeData, setLeadAssigneeData] = useState<any>(null); // Store lead assignee info

    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [showToast, setShowToast] = useState(false);

    const [cancelLoading, setCancelLoading] = React.useState(false);
    const [followUpLoading, setFollowUpLoading] = React.useState(false);
    const [isFetchingFollowUp, setIsFetchingFollowUp] = React.useState(false);

    const handleStudentSelectInModal = async (student: any) => {
        if (!userData) return;

        // Set the selected student in modal state
        setSelectedStudentInModalId(student.id);

        if (student.id) {
            // Fixed: Use student.id to create a proper document reference path
            // The path should be: companies/{companyId}/leads/{studentId}
            const leadRef = doc(db, `companies/${userData.companyId}/leads`, student.id);

            try {
                const leadDoc = await getDoc(leadRef);
                if (leadDoc.exists()) {
                    const leadData = leadDoc.data();

                    // Store the lead assignee data for later use
                    setLeadAssigneeData({
                        userId: leadData.assignedTo || leadData.userId, // Use assignedTo if available, fallback to userId
                        assignedUserName: leadData.assignedUserName || leadData.userName || 'Unknown'
                    });

                    if (leadData?.hasActiveFollowUp) {
                        // Get the active follow-up ID
                        const activeFollowUpId = leadData?.activeFollowUpId;
                        if (activeFollowUpId) {
                            // Start fetching state
                            setIsFetchingFollowUp(true);
                            
                            // Add 2-second delay
                            setTimeout(async () => {
                                try {
                                    setToastMessage("This student already has an active follow-up. Please complete it before adding a new one.");
                                    setIsErrorToast(true);
                                    setShowToast(true);

                                    // Populate the follow-up description and date time with the active follow-up details
                                    const followUpRef = doc(db, `companies/${userData.companyId}/followUps`, activeFollowUpId);
                                    const followUpDoc = await getDoc(followUpRef);
                                    if (followUpDoc.exists()) {
                                        const followUpData = followUpDoc.data();
                                        // update followUpData with the followUpDoc id
                                        followUpData.id = followUpDoc.id;
                                        setSelectedFollowUp(followUpData as FollowUp);
                                        setFollowUpDescription(followUpData.followUp || '');
                                        setFollowUpDateTime(format(followUpData.followUpTime.toDate(), "yyyy-MM-dd'T'HH:mm"));
                                        setToggleAddFollowUpAction('update');
                                    }
                                } catch (error) {
                                    console.error("Error fetching follow-up details:", error);
                                    setToastMessage("Error loading follow-up details.");
                                    setIsErrorToast(true);
                                    setShowToast(true);
                                } finally {
                                    // End fetching state after 2 seconds
                                    setIsFetchingFollowUp(false);
                                }
                            }, 2000);
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching lead document:", error);
                setToastMessage("Error checking student follow-up status.");
                setIsErrorToast(true);
                setShowToast(true);
            }
        }
    }

    useEffect(() => {
        handleStudentSelectInModal(selectedStudent);
    }, [selectedStudent, userData]);


    const handleClearForm = () => {
        setFollowUpDescription('');
        setFollowUpDateTime('');
        setToggleAddFollowUpAction('add');
        setSelectedFollowUp({});
        setToastMessage('Form cleared successfully!');
        setIsErrorToast(false);
        setShowToast(true);
    }

    const handleAddFollowUp = async (action: 'add' | 'update') => {

        if (!followUpDescription.trim() || !followUpDateTime || !userData || !currentUser) {
            setToastMessage('Please fill in all fields!'); setIsErrorToast(true); setShowToast(true);
            return;
        }

        try {
            setLoading(true);
            const followUpRef = collection(db, `companies/${userData.companyId}/followUps`);
            const leadRef = doc(db, `companies/${userData.companyId}/leads`, selectedStudentInModalId);

            if (!selectedStudentInModalId) {
                setToastMessage('Please select a student!'); setIsErrorToast(true); setShowToast(true);
                return;
            }

            // Verify lead exists and user has permission to modify it
            const leadDoc = await getDoc(leadRef);
            if (!leadDoc.exists()) {
                setToastMessage('Lead not found!'); setIsErrorToast(true); setShowToast(true);
                return;
            }

            if (action === 'add') {
                // Create a new follow-up document
                const newFollowUpTime = Timestamp.fromDate(new Date(followUpDateTime));

                const newFollowUp = {
                    followUp: followUpDescription.trim(),
                    followUpTime: newFollowUpTime,
                    isDone: false,
                    isEdited: false,
                    leadId: selectedStudentInModalId,
                    studentName: selectedStudent.name,
                    studentNumber: selectedStudent.phone,
                    userId: leadAssigneeData?.userId || '',
                    assignedUserName: userData.name || 'Unknown',
                    createdAt: Timestamp.now(),
                    createdBy: currentUser.uid, // Track who created the follow-up
                    originalAssigneeId: leadAssigneeData?.userId, // Store original assignee separately
                    originalAssigneeName: leadAssigneeData?.assignedUserName
                };

                // Add the new follow-up document
                const docRef = await addDoc(followUpRef, newFollowUp);

                // Update the follow-up document with its own ID
                await updateDoc(docRef, {
                    id: docRef.id
                });

                // update the leads document to set hasActiveFollowUp to true and activeFollowUpId to the new follow-up id
                await updateDoc(leadRef, {
                    hasActiveFollowUp: true,
                    activeFollowUpId: docRef.id,
                    activeFollowUpTime: newFollowUpTime,
                    activeFollowUpText: followUpDescription.trim(),
                    updatedAt: Timestamp.now(),
                    updatedBy: currentUser.uid
                });

                setToastMessage('Follow-up added successfully!'); setIsErrorToast(false); setShowToast(true);
                setTimeout(() => {
                    handleCloseModal();
                    // Refresh the page after successful follow-up addition
                    window.location.reload();
                },); // Close modal after 1.5 seconds to allow toast to be visible
                
            } else if (action === 'update') {
                // Verify the existing follow-up exists
                if (!selectedFollowUp?.id) {
                    setToastMessage('No existing follow-up found to update!'); setIsErrorToast(true); setShowToast(true);
                    return;
                }

                // First, mark the existing follow-up as done and edited (historical record)
                const existingFollowUpDocRef = doc(db, `companies/${userData.companyId}/followUps`, selectedFollowUp.id);
                
                // Check if the existing follow-up document exists
                const existingFollowUpDoc = await getDoc(existingFollowUpDocRef);
                if (!existingFollowUpDoc.exists()) {
                    setToastMessage('Existing follow-up not found!'); setIsErrorToast(true); setShowToast(true);
                    return;
                }

                await updateDoc(existingFollowUpDocRef, {
                    isDone: true,
                    isEdited: true,
                    updatedAt: Timestamp.now(),
                    updatedBy: currentUser.uid // Track who updated the follow-up
                });

                // Create a new follow-up with the updated details
                const newFollowUpTime = Timestamp.fromDate(new Date(followUpDateTime));
                const newFollowUp = {
                    followUp: followUpDescription.trim(),
                    followUpTime: newFollowUpTime,
                    isDone: false,
                    isEdited: false,
                    leadId: selectedStudentInModalId,
                    studentName: selectedStudent.name,
                    studentNumber: selectedStudent.phone,
                    userId: leadAssigneeData?.userId || '', 
                    assignedUserName: userData.name || 'Unknown',
                    createdAt: Timestamp.now(),
                    createdBy: currentUser.uid, 
                    originalFollowUpId: selectedFollowUp.id, 
                    originalAssigneeId: leadAssigneeData?.userId, 
                    originalAssigneeName: leadAssigneeData?.assignedUserName
                };

                // Add the new follow-up document
                const newDocRef = await addDoc(followUpRef, newFollowUp);

                // Update the new follow-up document with its own ID
                await updateDoc(newDocRef, {
                    id: newDocRef.id
                });

                // Update the lead document to point to the new active follow-up
                await updateDoc(leadRef, {
                    hasActiveFollowUp: true,
                    activeFollowUpId: newDocRef.id,
                    activeFollowUpTime: newFollowUpTime,
                    activeFollowUpText: followUpDescription.trim(),
                    updatedAt: Timestamp.now(),
                    updatedBy: currentUser.uid
                });

                setToastMessage('Follow-up updated successfully!'); setIsErrorToast(false); setShowToast(true);
                setTimeout(() => {
                    handleCloseModal();
                    // Refresh the page after successful follow-up update
                    window.location.reload();
                },); // Close modal after 1.5 seconds to allow toast to be visible
            }

        } catch (error: any) {
            console.error('Error adding follow-up:', error);
            
            // More specific error handling
            if (error.code === 'permission-denied') {
                setToastMessage('You do not have permission to perform this action. Please contact your administrator.'); 
            } else if (error.code === 'not-found') {
                setToastMessage('Document not found. Please refresh and try again.'); 
            } else if (error.code === 'unauthenticated') {
                setToastMessage('Authentication required. Please log in again.'); 
            } else {
                setToastMessage(`Error adding follow-up: ${error.message || 'Unknown error'}`); 
            }
            
            setIsErrorToast(true); 
            setShowToast(true);
        } finally {
            setLoading(false);
        }
    }

    const handleCloseModal = () => {
        setFollowUpDescription('');
        setFollowUpDateTime('');
        setSelectedStudentInModalId('');
        setToggleAddFollowUpAction('add');
        setSelectedFollowUp({});
        setLeadAssigneeData(null);
        onClose();
    }


    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            {isOpen && (
                <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4">
                    <div className="flex justify-between items-center mb-6">
                        {/* <h2 className="text-2xl font-montserrat text-gray-900">Add Follow Up</h2> */}
                        <button
                            onClick={() => handleCloseModal()}>
                            <img
                                src={`${process.env.PUBLIC_URL}/images/desktop/close.svg`}
                                alt="Close"
                                className="w-6 h-6"
                            />
                        </button>
                    </div>
                    <div className="space-y-4">
                        {/* Fetching message display */}
                        {isFetchingFollowUp && (
                            <div className="flex justify-center items-center py-8">
                                <div className="flex items-center gap-3">
                                    <svg
                                        className="animate-spin h-5 w-5 text-blue-600"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                        ></path>
                                    </svg>
                                    <span className="text-gray-600 font-medium">Fetching...</span>
                                </div>
                            </div>
                        )}
                        
                        {/* Form content - only show when not fetching */}
                        {!isFetchingFollowUp && (
                            <>
                                {/* Student Details Section */}
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="text-sm text-gray-500">Student Name</p>
                                            <p className="text-sm font-medium text-gray-900">{selectedStudent.name}</p>
                                        </div>

                                        {/* Clear Button */}
                                        {/* <div className="flex-shrink-0">
                                            <button
                                                onClick={handleClearForm}
                                                className="px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-800 transition-colors duration-200 flex items-center gap-2"
                                                title="Clear form fields"
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                    />
                                                </svg>
                                                Clear
                                            </button>
                                        </div> */}

                                        <div className="text-right">
                                            <p className="text-sm text-gray-500">Phone Number</p>
                                            <p className="text-sm font-medium text-gray-900">{selectedStudent.phone}</p>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Follow Up Description</label>
                                    <textarea
                                        value={followUpDescription}
                                        onChange={(e) => setFollowUpDescription(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                        rows={3}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Follow Up Date & Time</label>
                                    <input
                                        type="datetime-local"
                                        value={followUpDateTime}
                                        onChange={(e) => setFollowUpDateTime(e.target.value)}
                                        min={new Date().toISOString().slice(0, 16)}
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div className="flex justify-end gap-4 mt-6">
                                    {/* Cancel button */}
                                    <button
                                        onClick={async () => {
                                            setCancelLoading(true);
                                            try {
                                                await handleCloseModal();
                                            } finally {
                                                setCancelLoading(false);
                                            }
                                        }}
                                        disabled={cancelLoading}
                                        className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {cancelLoading && (
                                            <svg
                                                className="animate-spin h-5 w-5 text-gray-700"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                                ></path>
                                            </svg>
                                        )}
                                        {cancelLoading ? "Processing..." : "Cancel"}
                                    </button>

                                    {/* Add / Update button */}
                                    <button
                                        onClick={async () => {
                                            setFollowUpLoading(true);
                                            try {
                                                await handleAddFollowUp(toogleAddFollowUpAction);
                                            } finally {
                                                setFollowUpLoading(false);
                                            }
                                        }}
                                        disabled={followUpLoading}
                                        className="px-4 py-2 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{
                                            background: "linear-gradient(180deg, #3483BE 0%, #02385E 100%)",
                                        }}
                                    >
                                        {followUpLoading && (
                                            <svg
                                                className="animate-spin h-5 w-5 text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                                ></path>
                                            </svg>
                                        )}
                                        {followUpLoading
                                            ? "Processing..."
                                            : toogleAddFollowUpAction === "add"
                                                ? "Add Follow Up"
                                                : "Update Follow Up"}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={
                        toastMessage.includes('Error') ||
                        toastMessage.includes('Cannot') ||
                        toastMessage.includes('Failed')
                    }
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    )
}

export default FollowUpModal